import { useState, useEffect, useCallback, useRef } from "react";
import { message } from "antd";
import { getAllOperations } from "../../operations/services/operationService";
import { getRolePermissions, assignPermissionsToRole } from "../services/rolePermissionService";
import { PermissionTransferItem } from "../config/types";
import { getCurrentUserId } from "../utils/userUtils";

interface UsePermissionManagementProps {
  updateRolePermissionCount?: (roleId: string, count: number) => void;
}

interface UsePermissionManagementReturn {
  rolePermissions: PermissionTransferItem[];
  allPermissions: any[];
  permissionLoading: boolean;
  permissionTableLoading: boolean;
  saveLoading: boolean;
  fetchAllPermissions: () => Promise<void>;
  fetchRolePermissionsAndAll: (roleId: string) => Promise<void>;
  handlePermissionChange: (selectedIds: string[]) => void;
  handleSavePermissions: (roleId: string, selectedPermissions?: PermissionTransferItem[]) => Promise<void>;
  handleRemovePermission: (roleId: string, permissionId: string) => Promise<void>;
  setRolePermissions: (permissions: PermissionTransferItem[]) => void;
}

export const usePermissionManagement = (props?: UsePermissionManagementProps): UsePermissionManagementReturn => {
  const [rolePermissions, setRolePermissions] = useState<PermissionTransferItem[]>([]);
  const [allPermissions, setAllPermissions] = useState<any[]>([]);
  const [permissionLoading, setPermissionLoading] = useState<boolean>(false);
  const [permissionTableLoading, setPermissionTableLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  // 使用 ref 缓存 allPermissions，避免循环依赖
  const allPermissionsRef = useRef<any[]>([]);

  const fetchAllPermissions = useCallback(async () => {
    try {
      setPermissionLoading(true);
      const response = await getAllOperations();

      // 直接使用返回的权限数组
      if (Array.isArray(response)) {
        setAllPermissions(response);
        allPermissionsRef.current = response;
      } else {
        message.error("获取权限列表失败");
      }
    } catch (error) {
      message.error("获取所有权限失败");
    } finally {
      setPermissionLoading(false);
    }
  }, []);

  const fetchRolePermissionsAndAll = useCallback(async (roleId: string) => {
    if (!roleId) {
      return;
    }

    try {
      setPermissionTableLoading(true);
      console.log("开始获取角色权限，roleId:", roleId);

      // 获取角色权限（使用已缓存的allPermissions，不重复获取）
      const rolePermissionsResponse = await getRolePermissions(roleId);
      console.log("角色权限响应:", rolePermissionsResponse);

      // 使用ref中缓存的权限列表，如果为空则获取一次
      let currentAllPermissions = allPermissionsRef.current;
      if (currentAllPermissions.length === 0) {
        console.log("获取所有操作列表...");
        const allPermissionsResponse = await getAllOperations();
        console.log("所有操作响应:", allPermissionsResponse);
        if (Array.isArray(allPermissionsResponse)) {
          currentAllPermissions = allPermissionsResponse;
          setAllPermissions(currentAllPermissions);
          allPermissionsRef.current = currentAllPermissions;
        }
      }

      // 处理角色权限数据
      let rolePermissionList: any[] = [];
      if (rolePermissionsResponse?.data?.data && Array.isArray(rolePermissionsResponse.data.data)) {
        rolePermissionList = rolePermissionsResponse.data.data;
      } else if (Array.isArray(rolePermissionsResponse?.data)) {
        rolePermissionList = rolePermissionsResponse.data;
      } else if (
        rolePermissionsResponse?.data &&
        typeof rolePermissionsResponse.data === "object" &&
        "permissions" in rolePermissionsResponse.data
      ) {
        rolePermissionList = (rolePermissionsResponse.data as any).permissions;
      } else if (Array.isArray(rolePermissionsResponse)) {
        rolePermissionList = rolePermissionsResponse;
      }

      // 创建角色权限ID集合，用于标记哪些权限已被分配
      const rolePermissionIds = new Set(rolePermissionList.map((p) => p.id?.toString() || p._id?.toString()));
      console.log("角色权限ID集合:", Array.from(rolePermissionIds));
      console.log("角色权限列表:", rolePermissionList);

      // 构建完整的权限树形结构
      const permissionData: PermissionTransferItem[] = [];

      // 解析权限代码，提取服务、页面、操作信息
      const parsePermissionCode = (code: string) => {
        if (!code) return { service: "", page: "", action: "" };
        const parts = code.split(":");
        if (parts.length === 3) {
          return { service: parts[0], page: parts[1], action: parts[2] };
        } else if (parts.length === 2) {
          return { service: parts[0], page: "通用", action: parts[1] };
        }
        return { service: parts[0] || "", page: "通用", action: "" };
      };

      // 按微服务分组（使用当前的权限列表）
      console.log("所有权限列表:", currentAllPermissions);
      const permissionsByService = currentAllPermissions.reduce(
        (acc: Record<string, any[]>, permission: any) => {
          const serviceName = permission.serviceName || permission.service_name || "未分类服务";
          if (!acc[serviceName]) {
            acc[serviceName] = [];
          }
          acc[serviceName].push(permission);
          return acc;
        },
        {} as Record<string, any[]>
      );

      // 为每个微服务创建三层树形结构
      Object.entries(permissionsByService).forEach(([serviceName, permissions]: [string, any[]]) => {
        // 第一层：微服务节点
        const serviceNode: PermissionTransferItem = {
          id: `service_${serviceName}`,
          key: `service_${serviceName}`,
          title: `${serviceName}服务`,
          description: `${serviceName}服务的所有权限`,
          type: "MODULE",
          children: [],
          permissionData: {
            serviceName,
            type: "MODULE",
          },
        };

        // 按页面分组该服务的权限
        const permissionsByPage: Record<string, any[]> = {};
        permissions.forEach((permission: any) => {
          const parsed = parsePermissionCode(permission.code);
          const pageName = parsed.page || "通用";
          if (!permissionsByPage[pageName]) {
            permissionsByPage[pageName] = [];
          }
          permissionsByPage[pageName].push(permission);
        });

        // 为每个页面创建第二层节点
        Object.entries(permissionsByPage).forEach(([pageName, pagePermissions]: [string, any[]]) => {
          const pageNode: PermissionTransferItem = {
            id: `page_${serviceName}_${pageName}`,
            key: `page_${serviceName}_${pageName}`,
            title: `${pageName}`,
            description: `${pageName}页面的权限`,
            type: "MODULE",
            children: [],
            permissionData: {
              serviceName,
              page: pageName,
              type: "MODULE",
            },
          };

          // 为每个权限创建第三层操作节点
          pagePermissions.forEach((permission: any) => {
            const permissionId = permission.id?.toString() || permission._id?.toString();
            if (permissionId) {
              const isAssigned = rolePermissionIds.has(permissionId);
              const parsed = parsePermissionCode(permission.code);

              const permissionNode: PermissionTransferItem = {
                id: permissionId,
                key: permissionId,
                title: permission.name || parsed.action || `权限(${permissionId})`,
                description: permission.description || permission.code || "",
                code: permission.code || "",
                type: "FUNCTION",
                isAssigned,
                permissionData: {
                  ...permission,
                  id: permissionId,
                  name: permission.name || "未命名",
                  code: permission.code || "",
                  type: "FUNCTION",
                  serviceName: permission.serviceName || permission.service_name,
                },
              };
              pageNode.children!.push(permissionNode);
            }
          });

          // 只有当页面节点有子权限时才添加
          if (pageNode.children!.length > 0) {
            serviceNode.children!.push(pageNode);
          }
        });

        // 只有当服务节点有子页面时才添加
        if (serviceNode.children!.length > 0) {
          permissionData.push(serviceNode);
        }
      });

      setRolePermissions(permissionData);
    } catch (error) {
      console.error("获取权限失败:", error);
      message.error(`获取权限失败: ${error instanceof Error ? error.message : "未知错误"}`);
    } finally {
      setPermissionTableLoading(false);
    }
  }, []); // 移除allPermissions依赖，使用ref避免循环

  const handlePermissionChange = (selectedIds: string[]) => {
    // 更新权限树中每个节点的选中状态
    const updatePermissionTree = (permissions: PermissionTransferItem[]): PermissionTransferItem[] => {
      return permissions.map((permission) => {
        const isSelected = selectedIds.includes(permission.id);
        const updatedPermission = {
          ...permission,
          isAssigned: isSelected,
        };

        if (permission.children) {
          updatedPermission.children = updatePermissionTree(permission.children);
        }

        return updatedPermission;
      });
    };

    const updatedPermissions = updatePermissionTree(rolePermissions);
    setRolePermissions(updatedPermissions);
  };

  const handleSavePermissions = async (roleId: string, selectedPermissions?: PermissionTransferItem[]) => {
    console.log("=== 开始保存权限 ===");
    console.log("角色ID:", roleId);
    console.log("传入的选中权限:", selectedPermissions);

    if (!roleId) {
      message.warning("请先选择角色");
      return;
    }

    try {
      setSaveLoading(true);

      // 如果传入了selectedPermissions，直接使用其ID
      let validPermissionIds: string[] = [];

      if (selectedPermissions && selectedPermissions.length > 0) {
        // 直接提取ID，不依赖isAssigned属性
        validPermissionIds = selectedPermissions.filter((p) => p.type === "FUNCTION").map((p) => p.id);
        console.log("直接从传入权限提取ID:", validPermissionIds);
      } else {
        // 从树形结构中提取选中的权限ID
        const extractSelectedPermissionIds = (permissions: PermissionTransferItem[]): string[] => {
          const selectedIds: string[] = [];

          permissions.forEach((permission) => {
            if (permission.isAssigned && permission.type !== "MODULE") {
              selectedIds.push(permission.id);
              console.log("选中权限:", permission.title, permission.id);
            }

            if (permission.children) {
              selectedIds.push(...extractSelectedPermissionIds(permission.children));
            }
          });

          return selectedIds;
        };

        validPermissionIds = extractSelectedPermissionIds(rolePermissions);
        console.log("从rolePermissions提取ID:", validPermissionIds);
      }

      console.log("最终提取到的权限ID列表:", validPermissionIds);

      // 调用API保存权限
      console.log("调用API保存权限...");
      const response = await assignPermissionsToRole(roleId, validPermissionIds);
      console.log("API响应:", response);

      if (response.data.success) {
        message.success("权限分配成功");
        // 更新角色权限数量
        if (props?.updateRolePermissionCount) {
          props.updateRolePermissionCount(roleId, validPermissionIds.length);
        }
        console.log("✅ 权限保存成功");
      } else {
        message.error(response.data.message || "权限分配失败");
      }
    } catch (error) {
      console.error("保存权限失败:", error);
      message.error("权限分配失败");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleRemovePermission = async (roleId: string, permissionId: string) => {
    if (!roleId) {
      message.warning("请先选择角色");
      return;
    }

    try {
      // 由于后端没有删除单个权限的API，我们通过重新分配剩余权限来实现删除
      const remainingPermissions = rolePermissions.filter((p) => p.id !== permissionId).map((p) => p.id);

      // 重新分配剩余权限
      const response = await assignPermissionsToRole(roleId, remainingPermissions);

      if (response.data.success) {
        message.success("权限已从角色中移除");
        // 重新获取权限数据
        await fetchRolePermissionsAndAll(roleId);
      } else {
        message.error(response.data.message || "移除权限失败");
      }
    } catch (error) {
      message.error("移除角色权限失败");
    }
  };

  useEffect(() => {
    fetchAllPermissions();
  }, [fetchAllPermissions]);

  return {
    rolePermissions,
    allPermissions,
    permissionLoading,
    permissionTableLoading,
    saveLoading,
    fetchAllPermissions,
    fetchRolePermissionsAndAll,
    handlePermissionChange,
    handleSavePermissions,
    handleRemovePermission,
    setRolePermissions,
  };
};
